/*
  FlagLights - the yellow-flag "sector boards" band (client, 2026-10-10).
  Replaces the scrolling text marquee above "Enter Ultimate Lap Championship"
  on the landing page. Same geometry as the dark marquee it replaced
  (22px padding + 48px/72px display line + 1px borders = 118px on desktop),
  full-bleed black ground, brand yellow only. Nine 3x3 light boards; at random
  a board does the real-world double-flash of a yellow flag point, then goes
  dark again. Nothing scrolls, no words.

  Chosen from the client review (option C, sector boards) with: 2 extra boards (7 -> 9),
  roughly double the firing rate, boards scaled up to fill the rail height.
  Pauses off-screen / tab hidden; static (all lit) under reduced motion.
*/
import { useEffect, useRef } from "react";

const FLASH_MS = 1500; // matches gmFlagFlash duration in components.css

export default function FlagLights({ count = 9, every = 380, testid = "flag-lights" }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let on = true, timer = null;
    const timeouts = new Set();
    const later = (fn, ms) => { const t = setTimeout(() => { timeouts.delete(t); fn(); }, ms); timeouts.add(t); };

    const fire = () => {
      // hidden boards (phone trims the band to 5 via CSS) must not take a turn
      const boards = Array.from(el.querySelectorAll(".gm-flag__board:not(.is-on)")).filter((b) => b.offsetWidth > 0);
      if (!boards.length) return;
      const b = boards[Math.floor(Math.random() * boards.length)];
      b.classList.add("is-on");
      later(() => b.classList.remove("is-on"), FLASH_MS);
    };
    const tick = () => {
      fire();
      // one beat in four, a second board comes on a moment later (a sector going live)
      if (Math.random() < 0.25) later(fire, 140);
    };

    const start = () => { if (!timer && !reduced) timer = setInterval(tick, every); };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const update = () => (on && !document.hidden ? start() : stop());
    let io;
    if ("IntersectionObserver" in window) {
      io = new IntersectionObserver((e) => { on = !!e[0]?.isIntersecting; update(); }, { threshold: 0 });
      io.observe(el);
    }
    document.addEventListener("visibilitychange", update);
    update();
    return () => {
      stop();
      timeouts.forEach(clearTimeout);
      io?.disconnect();
      document.removeEventListener("visibilitychange", update);
    };
  }, [every]);

  return (
    <div className="gm-flag" ref={ref} data-testid={testid} aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <div className="gm-flag__board" key={i}>
          <i /><i /><i /><i /><i /><i /><i /><i /><i />
        </div>
      ))}
    </div>
  );
}
